/**
 * A MEZŐJAVÍTÁS MAGJA — tiszta függvények, React nélkül (#42).
 *
 * Miért külön fájl, és nem a horog belsejében: ugyanaz az érv, mint a
 * `rowMode.ts`-nél. Ez a három művelet — mi változott, mit küldünk, és mi
 * történik visszavonáskor — ÁLLAPOT NÉLKÜL eldönthető, tehát teszthető
 * anélkül, hogy egy komponenst fel kellene húzni hozzá.
 *
 * ═══ AMIT EZ A FÁJL NEM CSINÁL: NEM ÉRTELMEZ ═══
 *
 * ⚠️ A FORMÁTUM-ELLENŐRZÉS SZÁNDÉKOSAN A SZERVEREN VAN, EGYEDÜL. Az összeg-
 * és dátumértelmezés (`document-correction.ts`) huszonhárom szabály, és
 * közöttük olyanok, ahol a „majdnem jó" némán rossz adatot ír — az `1.234`
 * ezerkétszázharmincnégy, a `2026-02-31` pedig visszautasítás. Ha ezt a
 * felület IS elvégezné, két értelmező futna ugyanarra a bemenetre, és a
 * DRIFTJÜK csendes lenne: a felület átengedne valamit, amit a szerver
 * visszadob, vagy fordítva, visszautasítana valamit, amit a szerver elfogad.
 *
 * Ezért a felület csak ALAKOT néz (mi változott), és a hibát a szervertől
 * kapja, mezőnévvel együtt. Egy ilyen kör ára egy hálózati hívás — cserébe
 * egyetlen igazság van arról, mi érvényes összeg.
 */

import type { DocType } from '@/i18n/generated';

/** A javítható mezők — a backend `CORRECTABLE_FIELDS`-ének tükre. */
export type CorrectableField =
  | 'invoiceNumber'
  | 'partnerTaxNumber'
  | 'partnerName'
  | 'issueDate'
  | 'deliveryDate'
  // A SZÁMOLT időszak felülírása (#74) — `YYYY-MM`.
  | 'periodMonth'
  | 'grossAmount'
  | 'currency';

/** Egy mező a három forrásával — a `GET /documents/:id/fields` sora. */
export interface FieldView {
  fieldKey: CorrectableField;
  navValue: string | null;
  ocrValue: string | null;
  manualValue: string | null;
  /**
   * A SZÁMOLT érték — ma csak az időszakon (#74): a teljesítés vagy a kelt
   * hónapja. A „visszaállítom" erre is visszaesik. A többi mezőn `null`.
   */
  derivedValue: string | null;
  /** `manualValue ?? navValue ?? ocrValue ?? derivedValue` — ez látszik a bizonylaton. */
  effectiveValue: string | null;
}

export interface FieldsResponse {
  editable: boolean;
  fields: FieldView[];
  /** A bizonylat cégének engedélyezett típusai (#92, Viktor 2026-11-03). */
  docTypes: DocType[];
}

/** Amit a `PATCH /documents/:id/fields` vár. A `null` a VISSZAVONÁS. */
export interface FieldCorrection {
  fieldKey: CorrectableField;
  value: string | null;
}

/** A szerkesztés alatt álló szövegek, mezőnként. */
export type Draft = Record<CorrectableField, string>;

/**
 * A NYITÁS: a tárolt értékekből szöveg lesz.
 *
 * A `null` ÜRES SZTRING LESZ, és ez a beviteli mező természete — egy `null`-t
 * nem lehet begépelni. A visszaút (üres sztring → `null`) a `changesOf`-ban
 * áll, és az a fájl egyetlen helye, ahol ez a leképezés megfordul.
 */
export function draftFrom(fields: readonly FieldView[]): Draft {
  const draft = {} as Draft;
  for (const field of fields) {
    draft[field.fieldKey] = field.effectiveValue ?? '';
  }
  return draft;
}

/**
 * MI VÁLTOZOTT — és CSAK az megy el.
 *
 * ⚠️ AMIT NEM KÜLDÜNK EL, AZ VÉDETT. A szerver az `undefined`-et úgy érti,
 * hogy „ne nyúlj hozzá" (`document-field.service.ts`), tehát egy változatlan
 * mező kihagyása megőrzi, ami ott áll. Ha MINDEN mezőt elküldenénk, egy
 * érintetlen mező is `manualValue`-t kapna — és onnantól „javítottnak"
 * látszana, holott a könyvelő hozzá sem nyúlt. A származás csendben elveszne.
 *
 * ÖSSZEHASONLÍTÁS KÖRBEVÁGÁS UTÁN. A mezőbe tévedt szóköz nem változás; a
 * szerver úgyis levágná, és akkor egy üres köteg menne el „1 mező változott"
 * felirat mellett.
 */
export function changesOf(fields: readonly FieldView[], draft: Draft): FieldCorrection[] {
  const changes: FieldCorrection[] = [];

  for (const field of fields) {
    const typed = (draft[field.fieldKey] ?? '').trim();
    const stored = field.effectiveValue ?? '';

    if (typed === stored.trim()) {
      continue;
    }

    // AZ ÜRES MEZŐ `null`, NEM ÜRES SZTRING. Ez a javítás VISSZAVONÁSA: a sor
    // visszaesik arra, amit az OCR vagy a NAV látott. Egy üres sztring örökre
    // elrejtené a kinyert értéket egy láthatatlan javítás mögé.
    // ⚠️ A SZÁMOLT ÉRTÉK BEÍRÁSA NEM FELÜLÍRÁS (#74). A „visszaállítom" az
    // időszakon a számolt hónapot teszi a mezőbe; ha az kézi értékként menne
    // el, a hónap RÖGZÜLNE, és egy későbbi dátumjavítás már nem mozdítaná. A
    // `null` a felülírás visszavonása — a szerver onnan a dátumból számol.
    const derived = field.derivedValue?.trim() ?? null;
    const value = typed === '' || (derived !== null && typed === derived) ? null : typed;
    changes.push({ fieldKey: field.fieldKey, value });
  }

  return changes;
}

/**
 * VAN-E MIRE VISSZAESNI — ezen áll a „visszaállítom".
 *
 * Csak akkor van értelme visszavonni, ha a könyvelő tényleg felülírt valamit,
 * ÉS a gép látott ott valamit. Javítás nélkül nincs mit visszavonni; gépi
 * érték nélkül a visszavonás üresre vinné a mezőt, és az nem „visszaállítás",
 * hanem törlés — arra a mező kiürítése való, saját következménnyel.
 */
export function machineValueOf(field: FieldView): string | null {
  if (field.manualValue === null) {
    return null;
  }
  return field.navValue ?? field.ocrValue ?? field.derivedValue;
}

/**
 * A MEZŐK SORRENDJE A KÉPERNYŐN.
 *
 * ⚠️ NEM A BACKEND SORRENDJE, és ez szándékos. A szerver a tárolás rendjében
 * adja ki őket; a képernyőn viszont az OLVASÁS rendje számít — a partner és a
 * számlaszám fent, a pénz lent —, és a `MunkalistaCBordo` fejléce is így áll.
 * A kettő külön él, tehát a szerver bővülése nem rendezi át a képernyőt.
 *
 * ⚠️ AMI ITT NINCS, AZ NEM JELENIK MEG. A drift-őr a tesztben áll: ha a
 * szerver új javítható mezőt kap, és ide nem kerül be, a könyvelő nem tudná
 * javítani — csendben.
 */
export const FIELD_ORDER: readonly CorrectableField[] = [
  'partnerName',
  'partnerTaxNumber',
  'invoiceNumber',
  'issueDate',
  'deliveryDate',
  'grossAmount',
  'currency',
  // A PÉNZ-SOR UTÁN (#74): a rács három hasábos, a bruttó kettőt foglal — így
  // a bruttó és a pénznem egy sorban marad, az időszak a következőt nyitja.
  'periodMonth',
];

/** A képernyő-sorrendbe rakott mezők. Ismeretlen kulcs a végére kerül. */
export function inDisplayOrder(fields: readonly FieldView[]): FieldView[] {
  return [...fields].sort((a, b) => rank(a.fieldKey) - rank(b.fieldKey));
}

function rank(fieldKey: CorrectableField): number {
  const index = FIELD_ORDER.indexOf(fieldKey);
  return index === -1 ? FIELD_ORDER.length : index;
}
